import { useEffect, useRef, useState } from "react";
import { animate, stagger, onScroll } from "animejs";
import { useTranslation } from "react-i18next";
import useSectionAnalytics, { mergeRefs } from "../useSectionAnalytics";

// ids/icons are identical across languages — not translated content.
const STEP_META = [
  { id: 1, key: "book", icon: "/icons/booking.svg" },
  { id: 2, key: "review", icon: "/icons/doctor.svg" },
  { id: 3, key: "video", icon: "/icons/video-consultation.svg" },
  { id: 4, key: "prescription", icon: "/icons/e-prescription.svg" },
  { id: 5, key: "delivery", icon: "/icons/delivery.svg" },
];

function useIsDesktop(breakpoint = 768) {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= breakpoint
  );

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [breakpoint]);

  return isDesktop;
}

function useWorkflowContent() {
  const { t } = useTranslation();
  const steps = t("workflow.steps", { returnObjects: true });
  return {
    title: t("workflow.title"),
    steps: STEP_META.map(({ id, key, icon }) => ({
      id,
      icon,
      title: steps[key].title,
      desc: steps[key].desc,
    })),
  };
}

export default function Workflow() {
  const tx = useWorkflowContent();
  const analyticsRef = useSectionAnalytics("workflow");
  const isDesktop = useIsDesktop();

  return isDesktop ? (
    <WorkflowScrolly tx={tx} sectionRef={analyticsRef} />
  ) : (
    <WorkflowCompact tx={tx} sectionRef={analyticsRef} />
  );
}

// ---------------------------------------------------------------------------
// Desktop / tablet — pinned scrollytelling stage. The section is 5 viewports
// tall; while it scrolls past, a sticky inner stage stays put and a rail on
// the left tracks scroll progress, lighting up one step at a time in the
// showcase on the right.
// ---------------------------------------------------------------------------
function WorkflowScrolly({ tx, sectionRef }) {
  const wrapperRef = useRef(null);
  const railFillRef = useRef(null);
  const iconWrapRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const indexRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const prevIndexRef = useRef(-1);

  useEffect(() => {
    if (!wrapperRef.current) return undefined;

    const observer = onScroll({
      target: wrapperRef.current,
      enter: "top top",
      leave: "bottom bottom",
      onUpdate: (self) => {
        const progress = self.progress;
        if (railFillRef.current) {
          railFillRef.current.style.transform = `scaleY(${progress})`;
        }
        const idx = Math.min(
          tx.steps.length - 1,
          Math.max(0, Math.floor(progress * tx.steps.length))
        );
        if (idx !== activeIndexRef.current) {
          activeIndexRef.current = idx;
          setActiveIndex(idx);
        }
      },
    });

    return () => observer.revert();
  }, [tx.steps.length]);

  // Per-step "arrival" transition in the showcase panel.
  useEffect(() => {
    if (prevIndexRef.current === activeIndex) return;
    prevIndexRef.current = activeIndex;

    if (iconWrapRef.current) {
      animate(iconWrapRef.current, {
        clipPath: ["inset(0 100% 0 0)", "inset(0 0% 0 0)"],
        duration: 700,
        ease: "outExpo",
      });
    }
    if (titleRef.current) {
      animate(titleRef.current, {
        opacity: [0, 1],
        translateY: [24, 0],
        duration: 500,
        ease: "outQuad",
      });
    }
    if (descRef.current) {
      animate(descRef.current, {
        opacity: [0, 1],
        translateY: [20, 0],
        duration: 500,
        delay: 100,
        ease: "outQuad",
      });
    }
    if (indexRef.current) {
      animate(indexRef.current, {
        opacity: [0, 1],
        translateX: [-16, 0],
        duration: 500,
        ease: "outQuad",
      });
    }
  }, [activeIndex]);

  const step = tx.steps[activeIndex];

  return (
    <section
      id="workflow"
      ref={mergeRefs(sectionRef, wrapperRef)}
      className="workflow-scrolly"
    >
      <div className="workflow-scrolly__pin">
        <div className="workflow-scrolly__glow" />

        <h2 className="workflow-scrolly__title">{tx.title}</h2>

        <div className="workflow-scrolly__stage">
          <div className="workflow-rail">
            <div className="workflow-rail__track">
              <div className="workflow-rail__fill" ref={railFillRef} />
            </div>

            {tx.steps.map((s, i) => (
              <div
                key={s.id}
                className={`workflow-rail__item ${
                  i === activeIndex ? "is-active" : ""
                } ${i < activeIndex ? "is-done" : ""}`}
              >
                <span className="workflow-rail__dot">{s.id}</span>
                <span className="workflow-rail__label">{s.title}</span>
              </div>
            ))}
          </div>

          <div className="workflow-showcase">
            <span className="workflow-showcase__index" ref={indexRef}>
              0{activeIndex + 1}
            </span>

            <div className="workflow-showcase__frame">
              <div className="workflow-showcase__ring" />
              <div className="workflow-showcase__icon-wrap" ref={iconWrapRef}>
                <img
                  src={step.icon}
                  alt={step.title}
                  className="workflow-showcase__icon"
                />
              </div>
            </div>

            <h3 className="workflow-showcase__label" ref={titleRef}>
              {step.title}
            </h3>

            <p className="workflow-showcase__desc" ref={descRef}>
              {step.desc}
            </p>
          </div>
        </div>

        <div className="workflow-scrolly__hint">SCROLL</div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Mobile — pinning 5 viewports' worth of scroll doesn't work well on small
// touch screens, so this is a compact, non-pinned reveal instead: a
// connecting line draws in with a traveling glow dot, and each step's badge
// pops in with an elastic bounce as the section enters view.
// ---------------------------------------------------------------------------
function WorkflowCompact({ tx, sectionRef }) {
  const [visible, setVisible] = useState(false);
  const rootRef = useRef(null);
  const lineFillRef = useRef(null);
  const lineDotRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      const section = document.querySelector(".workflow");
      if (section) {
        const top = section.getBoundingClientRect().top;
        const height = window.innerHeight;
        if (top < height * 0.8) {
          setVisible(true);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!visible || hasAnimatedRef.current || !rootRef.current) return;
    hasAnimatedRef.current = true;

    const root = rootRef.current;
    const badges = root.querySelectorAll(".step-badge");
    const icons = root.querySelectorAll(".step-icon");
    const titles = root.querySelectorAll(".workflow-step h3, .workflow-step .step-desc");
    const lineDuration = 1100;

    animate(lineFillRef.current, {
      scaleX: [0, 1],
      duration: lineDuration,
      ease: "outCubic",
    });

    if (lineDotRef.current) {
      animate(lineDotRef.current, {
        left: ["0%", "100%"],
        opacity: [0, 1, 1, 0],
        duration: lineDuration,
        ease: "outCubic",
        onComplete: () => {
          lineDotRef.current.style.opacity = "";
        },
      });
    }

    animate(badges, {
      scale: [0, 1],
      rotate: [-25, 0],
      duration: 650,
      delay: stagger(160, { start: 150 }),
      ease: "outElastic(1, .55)",
      onComplete: (a) => a.targets.forEach((el) => { el.style.transform = ""; }),
    });

    animate(icons, {
      opacity: [0, 0.7],
      scale: [0.5, 1],
      duration: 500,
      delay: stagger(160, { start: 350 }),
      ease: "outQuad",
      onComplete: (a) => a.targets.forEach((el) => { el.style.opacity = ""; el.style.transform = ""; }),
    });

    animate(titles, {
      opacity: [0, 1],
      translateY: [16, 0],
      duration: 500,
      delay: stagger(160, { start: 450 }),
      ease: "outQuad",
      onComplete: (a) => a.targets.forEach((el) => { el.style.opacity = ""; el.style.transform = ""; }),
    });
  }, [visible]);

  return (
    <section
      id="workflow"
      ref={mergeRefs(sectionRef, rootRef)}
      className={`workflow ${visible ? "visible" : ""}`}
    >
      <h2>{tx.title}</h2>

      <div className="workflow-grid">
        <div className="workflow-line">
          <div className="workflow-line__fill" ref={lineFillRef} />
          <div className="workflow-line__dot" ref={lineDotRef} />
        </div>

        {tx.steps.map((step) => (
          <div className="workflow-step" key={step.id}>
            <div className="step-badge">{step.id}</div>
            <img src={step.icon} alt={step.title} className="step-icon" />
            <h3>{step.title}</h3>
            <p className="step-desc">{step.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
